import {
  Lock,
  LockOpen,
  Paperclip,
  Plus,
  RefreshCw,
  SquarePen,
  StickyNote,
  Tag,
  Trash2,
} from "lucide-react";

import RvxTag from "components/RvxTag";
import { startCase, titleCase } from "helpers/string";
import EntryDetails from "components/LogItem/elements/EntryDetails";
import {
  CUSTOM_FIELD_CHANGES,
  flattenChanges,
} from "components/LogItem/messages/changes";
import { getInfoChangedMessage } from "components/LogItem/messages/changesMessage";
import {
  MANUAL_ACTIVITY_NAME_MAPPINGS,
  getGenericMessage,
} from "components/LogItem/messages/genericMessage";
import { importSuffix } from "components/LogItem/messages/importSuffix";
import { renderInitialValues } from "components/LogItem/messages/initialValues";
import {
  getModelLink,
  parseCustomTableAuditableType,
} from "components/LogItem/messages/modelLinks";
import { renderAttachmentCreatedMessage } from "components/LogItem/events/attachmentEvents";
import {
  CAMPAIGN_JOIN_TYPES,
  campaignMemberType,
  renderAddedToCampaignMessage,
} from "components/LogItem/events/campaignEvents";
import { renderNoteCreatedMessage } from "components/LogItem/events/noteEvents";

function titleForEvent({
  category,
  auditable_type,
  custom_table_singular_name,
}) {
  const ctSlug = parseCustomTableAuditableType(auditable_type);
  if (ctSlug) {
    return custom_table_singular_name || startCase(ctSlug);
  }
  if (auditable_type === "Lead") {
    return "CRM Lead";
  }
  return titleCase(category);
}

export const RECORD_EVENTS = {
  custom_field_modified: {
    title: "Custom Field Modified (Bulk)",
    icon: SquarePen,
    message: ({
      user_name,
      contact_id,
      contact_name,
      entity_id,
      entity_name,
      info: { changes },
    }) => {
      changes = flattenChanges(changes, {
        custom_fields: CUSTOM_FIELD_CHANGES,
      });
      return getInfoChangedMessage({
        user_name,
        contact_id,
        contact_name,
        entity_id,
        entity_name,
        changes,
      });
    },
  },
  custom_field_deleted: {
    title: "Custom Field Deleted (Bulk)",
    icon: Trash2,
    message: ({
      user_name,
      contact_id,
      contact_name,
      entity_id,
      entity_name,
      info: { changes },
    }) => {
      changes = flattenChanges(changes, {
        custom_fields: CUSTOM_FIELD_CHANGES,
      });
      return getInfoChangedMessage({
        user_name,
        contact_id,
        contact_name,
        entity_id,
        entity_name,
        changes,
      });
    },
  },
  tag_changed: {
    title: ({ info }) => (info.removed ? "Tag Removed" : "Tag Added"),
    icon: Tag,
    message: ({
      user_name,
      contact_name,
      entity_name,
      auditable_type,
      auditable_id,
      case_name,
      case_id,
      info: { removed, updated_tag, data_job_id, machine_id },
    }) => {
      const typeMappings = {
        Contact: contact_name,
        Entity: entity_name,
        Case: case_name,
        Machine: machine_id,
        Lead: "the CRM lead",
      };
      const typeName = typeMappings[auditable_type];
      const target = typeName
        ? getModelLink(auditable_id, auditable_type, {
            label: typeName,
            machine_id,
            case_id,
          })
        : `${auditable_type} (ID: ${auditable_id})`;

      return (
        <div>
          {user_name || "System"} {removed ? "removed" : "added"} the tag{" "}
          <RvxTag tag={updated_tag} /> {removed ? "from" : "to"} {target}
          {importSuffix(data_job_id)}.
        </div>
      );
    },
  },
  created: {
    title: item => {
      if (item.auditable_type === "Attachment") return "Attachment Created";
      if (item.auditable_type === "ActivityLog") return "Note Created";
      if (CAMPAIGN_JOIN_TYPES.includes(item.auditable_type)) {
        return `${campaignMemberType(item)} added to Campaign`;
      }
      return `${titleForEvent(item)} Created`;
    },
    icon: item => {
      if (item.auditable_type === "Attachment") return Paperclip;
      if (item.auditable_type === "ActivityLog") return StickyNote;
      return Plus;
    },
    message: item => {
      // Attachment/ActivityLog now flow through the generic "created" action; the
      // server enriches the same attachment_info/model_id/model_type as the legacy
      // dedicated logs, so render them identically.
      if (item.auditable_type === "Attachment") {
        return renderAttachmentCreatedMessage(item);
      }
      if (item.auditable_type === "ActivityLog") {
        return renderNoteCreatedMessage(item);
      }
      if (CAMPAIGN_JOIN_TYPES.includes(item.auditable_type)) {
        return renderAddedToCampaignMessage(item);
      }
      const {
        user_name,
        contact_id,
        contact_name,
        entity_id,
        entity_name,
        case_id,
        case_name,
        info: {
          value,
          data_job_id,
          revenue_opportunity_id,
          tag,
          updated_tag,
          field_name,
          initial_values,
        },
        auditable_type,
        campaign_id,
        campaign_name,
        auditable_id,
        custom_table_singular_name,
      } = item;
      return (
        <>
          {getGenericMessage({
            user_name,
            contact_id,
            contact_name,
            entity_id,
            entity_name,
            auditable_type,
            // Unlike the "changed" logs, a machine's identifier lives in the
            // creation snapshot rather than at the top of info.
            machine_id:
              auditable_type === "Machine" ? initial_values?.machine_id : null,
            action: "created",
            value,
            data_job_id,
            tag,
            updated_tag,
            field_name,
            case_id,
            case_name,
            revenue_opportunity_id,
            campaign_id,
            campaign_name,
            auditable_id,
            custom_table_singular_name,
          })}
          {renderInitialValues(initial_values, {
            auditableType: auditable_type,
          })}
        </>
      );
    },
  },
  changed: {
    title: item => `${titleForEvent(item)} Changed`,
    icon: SquarePen,
    message: ({
      user_name,
      contact_id,
      contact_name,
      entity_id,
      entity_name,
      case_id,
      case_name,
      campaign_name,
      info,
      auditable_type,
      auditable_id,
      custom_table_singular_name,
    }) => {
      let { value, changes, data_job_id } = info;
      changes = flattenChanges(changes, {
        phone_numbers: {
          label: "phone number",
        },
        sessions: {
          label: "session",
        },
        custom_fields: CUSTOM_FIELD_CHANGES,
        addresses: {
          label: "address",
        },
        is_emailable: {
          mapLine: ({ value }) => `Made${value ? "" : " not"} emailable`,
        },
        is_callable: {
          mapLine: ({ value }) => `Made${value ? "" : " not"} callable`,
        },
        enable_email: {
          mapLine: ({ value }) => `${value ? "Enabled" : "Disabled"} email`,
        },
        enable_call: {
          mapLine: ({ value }) => `${value ? "Enabled" : "Disabled"} call`,
        },
      });
      return getInfoChangedMessage({
        user_name,
        contact_id,
        contact_name,
        entity_id,
        entity_name,
        changes,
        auditable_type,
        auditable_id,
        value,
        data_job_id,
        case_id,
        case_name,
        campaign_name,
        info,
        custom_table_singular_name,
      });
    },
  },
  deleted: {
    // A manual text message names its channel, which the category cannot.
    title: item => {
      const messageType = item.info.message_type;
      if (item.auditable_type === "TextMessage" && messageType) {
        const channel =
          MANUAL_ACTIVITY_NAME_MAPPINGS[messageType] || startCase(messageType);
        return `${channel} Message Deleted`;
      }
      return `${titleForEvent(item)} Deleted`;
    },
    icon: Trash2,
    message: ({
      user_name,
      campaign_id,
      campaign_name,
      contact_id,
      contact_name,
      entity_id,
      entity_name,
      case_id,
      case_name,
      info: { value, data_job_id, revenue_opportunity_id, message_type },
      auditable_type,
      auditable_id,
      custom_table_singular_name,
    }) => {
      return getGenericMessage({
        user_name,
        contact_id,
        contact_name,
        entity_id,
        entity_name,
        auditable_type,
        auditable_id,
        action: "deleted",
        value,
        data_job_id,
        case_id,
        case_name,
        campaign_id,
        campaign_name,
        revenue_opportunity_id,
        message_type,
        custom_table_singular_name,
      });
    },
  },
  manual_update: {
    title: "Manual Update",
    icon: RefreshCw,
    message: ({ info }) => (
      <>
        <p>The {info.column} column was updated manually.</p>
        <EntryDetails
          rows={[
            { label: "Old Value", value: info.old_value },
            { label: "New Value", value: info.new_value },
          ]}
        />
      </>
    ),
  },
  revenue_opportunity_locked: {
    title: "Revenue Opportunity Locked",
    icon: Lock,
    message: ({
      user_name,
      auditable_type,
      auditable_id,
      info: { changes } = {},
    }) => {
      changes = flattenChanges(changes);
      return getInfoChangedMessage({
        user_name,
        auditable_type,
        auditable_id,
        changes,
      });
    },
  },
  revenue_opportunity_unlocked: {
    title: "Revenue Opportunity Unlocked",
    icon: LockOpen,
    message: ({
      user_name,
      auditable_type,
      auditable_id,
      info: { changes } = {},
    }) => {
      changes = flattenChanges(changes);
      return getInfoChangedMessage({
        user_name,
        auditable_type,
        auditable_id,
        changes,
      });
    },
  },
};
